import { useEffect, useMemo, useState } from 'react';
import { businessConfig } from '../../../config/businessConfig';
import { additionalServices, services } from '../../../data/services';
import { buildConsultationMessage, buildWhatsAppUrl } from '../../../utils/whatsapp';
import { formatInputDate, getTodayInputValue } from '../../../utils/helpers';

/** Genera franjas horarias de 08:00 a 21:30 en intervalos de 30 min. */
const timeSlots = (() => {
  const slots = [];
  for (let h = 8; h <= 21; h++) {
    for (const m of [0, 30]) {
      if (h === 22 && m > 0) break;
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
})();

const initialForm = {
  customerName: '',
  serviceId: '',
  preferredDate: '',
  timeBlock: '',
  specificTime: '',
  removal: 'No',
  extras: [],
  designNotes: '',
  hasReference: false,
};

export default function Consultation({ selectedServiceId }) {
  const [form, setForm] = useState(initialForm);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (selectedServiceId) setForm((current) => ({ ...current, serviceId: selectedServiceId }));
  }, [selectedServiceId]);

  const selectedService = services.find((service) => service.id === form.serviceId);
  const timePreference = form.timeBlock === 'Hora específica'
    ? form.specificTime || 'Hora específica por definir'
    : form.timeBlock;

  const message = useMemo(() => buildConsultationMessage({
    customerName: form.customerName,
    serviceName: selectedService?.name || '',
    preferredDate: formatInputDate(form.preferredDate),
    timePreference,
    removal: form.removal,
    extras: form.extras.map((id) => additionalServices.find((item) => item.id === id)?.name).filter(Boolean),
    designNotes: form.designNotes,
    hasReference: form.hasReference,
  }), [form, selectedService, timePreference]);

  const whatsappUrl = buildWhatsAppUrl(message);
  const canSend = Boolean(form.customerName.trim() && form.serviceId && form.preferredDate && form.timeBlock);

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleExtra = (id) => {
    setForm((current) => ({
      ...current,
      extras: current.extras.includes(id)
        ? current.extras.filter((extraId) => extraId !== id)
        : [...current.extras, id],
    }));
  };

  return (
    <section className="section consultation" id="consulta">
      <div className="container consultation__layout">
        <div className="consultation__intro">
          <p className="eyebrow">Consulta guiada</p>
          <h2 className="section-title">Cuéntanos qué tienes en mente.</h2>
          <p>Prepara tu consulta en pocos pasos. El mensaje se abrirá en WhatsApp para que puedas revisarlo antes de enviarlo.</p>
          <div className="consultation__promise">
            <span aria-hidden="true">01</span>
            <p>No guardamos tus datos. Toda la información permanece en tu dispositivo y sólo se comparte cuando decides abrir WhatsApp.</p>
          </div>
        </div>

        <form className="consultation-form" onSubmit={(event) => event.preventDefault()}>
          <div className="field">
            <label htmlFor="customerName">Tu nombre <span aria-hidden="true">*</span></label>
            <input id="customerName" name="customerName" value={form.customerName} onChange={updateField} autoComplete="name" required />
          </div>

          <div className="field">
            <label htmlFor="serviceId">Servicio de interés <span aria-hidden="true">*</span></label>
            <select id="serviceId" name="serviceId" value={form.serviceId} onChange={updateField} required>
              <option value="">Selecciona un servicio</option>
              {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label htmlFor="preferredDate">Fecha que prefieres <span aria-hidden="true">*</span></label>
            <input id="preferredDate" name="preferredDate" type="date" min={getTodayInputValue()} value={form.preferredDate} onChange={updateField} required />
          </div>

          <fieldset className="field fieldset">
            <legend>Horario que prefieres <span aria-hidden="true">*</span></legend>
            <div className="choice-row">
              {['Mañana', 'Tarde', 'Hora específica'].map((option) => (
                <label className={`choice ${form.timeBlock === option ? 'choice--selected' : ''}`} key={option}>
                  <input type="radio" name="timeBlock" value={option} checked={form.timeBlock === option} onChange={updateField} />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            <select
              className="field__nested"
              aria-label="Hora específica preferida"
              name="specificTime"
              value={form.specificTime}
              onChange={updateField}
              style={form.timeBlock !== 'Hora específica' ? { display: 'none' } : undefined}
            >
              <option value="">Selecciona una hora</option>
              {timeSlots.map((slot) => (
                <option key={slot} value={slot}>{slot} hrs</option>
              ))}
            </select>
          </fieldset>

          <fieldset className="field fieldset">
            <legend>¿Necesitas retiro de producto?</legend>
            <div className="choice-row">
              {['No', 'Sí, de Mujer Virtuosa', 'Sí, de otro centro'].map((option) => (
                <label className={`choice ${form.removal === option ? 'choice--selected' : ''}`} key={option}>
                  <input type="radio" name="removal" value={option} checked={form.removal === option} onChange={updateField} />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="field fieldset">
            <legend>¿Te interesa agregar algo?</legend>
            <div className="choice-row">
              {additionalServices.slice(0, 3).map((extra) => (
                <label className={`choice ${form.extras.includes(extra.id) ? 'choice--selected' : ''}`} key={extra.id}>
                  <input type="checkbox" checked={form.extras.includes(extra.id)} onChange={() => toggleExtra(extra.id)} />
                  <span>{extra.name}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="field">
            <label htmlFor="designNotes">Idea o referencia del diseño <small>(opcional)</small></label>
            <textarea id="designNotes" name="designNotes" rows="4" maxLength="500" value={form.designNotes} onChange={updateField} placeholder="Cuéntanos sobre colores, largo o estilo que tienes en mente." />
            <span className="field__counter">{form.designNotes.length}/500</span>
          </div>

          <label className="check-line">
            <input type="checkbox" name="hasReference" checked={form.hasReference} onChange={updateField} />
            <span>Tengo una imagen de referencia y la enviaré al abrir WhatsApp.</span>
          </label>

          <p className="consultation-form__notice">{businessConfig.consultationDisclaimer}</p>

          <div className="consultation-form__actions">
            <button className="button button--line" type="button" onClick={() => setShowPreview((current) => !current)} disabled={!canSend}>
              {showPreview ? 'Ocultar mensaje' : 'Revisar mensaje'}
            </button>
            <a
              className={`button button--gold ${!canSend ? 'button--disabled' : ''}`}
              href={canSend ? whatsappUrl : undefined}
              target="_blank"
              rel="noreferrer"
              aria-disabled={!canSend}
              aria-label="Enviar a Poleth la consulta preparada"
            >
              Enviar mensaje
            </a>
          </div>

          <pre className="message-preview" aria-live="polite" style={!(showPreview && canSend) ? { display: 'none' } : undefined}>{message}</pre>
        </form>
      </div>
    </section>
  );
}
